// src/pages/CompleteRegistration.js
// v8 – calmer account-first copy for principal completion
//
// Changes vs v7:
// ✅ Rewords page to feel like account finishing, not full onboarding
// ✅ Renames display-name language to user-facing "Your name"
// ✅ Keeps token flow, redirect logic, and resend behavior intact

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import ResendRegistrationLinkBanner from "../components/ResendRegistrationLinkBanner";
import "../styles/CompleteRegistration.css";

const CompleteRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token") || "";
    setToken(t);

    if (t) {
      try {
        const decoded = jwtDecode(t);
        if (decoded?.email) setEmail(decoded.email);
      } catch {
        // ignore
      }
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowResend(false);

    const cleanDisplayName = displayName.trim();

    if (!token) {
      setError("Missing registration token.");
      return;
    }
    if (!cleanDisplayName) {
      setError("Your name is required.");
      return;
    }
    if (cleanDisplayName.length > 255) {
      setError("Your name must be 255 characters or less.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post(API_PATHS.COMPLETE_REGISTRATION, {
        token,
        display_name: cleanDisplayName,
        new_password: password,
      });

      const access = data?.access_token;
      const refresh = data?.refresh_token;

      if (!access) {
        throw new Error("Registration succeeded but no token returned.");
      }

      localStorage.setItem("token", access);
      if (refresh) localStorage.setItem("refreshToken", refresh);

      const decoded = jwtDecode(access);
      const role = decoded?.role?.toUpperCase() || "";
      const schoolName = decoded?.school_name || "";

      localStorage.setItem("role", role);
      localStorage.setItem("schoolName", schoolName);

      window.dispatchEvent(new Event("gg-auth-changed"));

      if (role === "ADMIN") {
        navigate(ROUTES.ADMIN, { replace: true });
        return;
      }

      if (role === "PRINCIPAL" || role === "STAFF") {
        navigate(ROUTES.CONTINUE_IN_APP, {
          replace: true,
          state: {
            email: email || "",
            role,
          },
        });
        return;
      }

      navigate(ROUTES.VIEW_PHOTOS, { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";

      setError(typeof detail === "string" ? detail : JSON.stringify(detail));

      if (String(detail).toLowerCase().includes("expired")) {
        setShowResend(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="complete-registration-page">
      <div className="complete-registration-container">
        <div className="complete-registration-badge">Almost there</div>

        <h2 className="form-title">Finish setting up your account</h2>

        <p className="complete-registration-subtitle">
          Add your name and choose a password.
        </p>

        {error && (
          <p className="error-message" aria-live="polite">
            {error}
          </p>
        )}

        {showResend && email && (
          <div className="resend-banner-wrapper">
            <ResendRegistrationLinkBanner email={email} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-group">
            <label className="form-label" htmlFor="complete-registration-email">
              Your email
            </label>
            <input
              id="complete-registration-email"
              className="form-input form-input--readonly"
              type="email"
              value={email || ""}
              readOnly
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="complete-registration-display-name">
              Your name
            </label>
            <input
              id="complete-registration-display-name"
              className="form-input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="complete-registration-password">
              Password
            </label>
            <input
              id="complete-registration-password"
              className="form-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              autoComplete="new-password"
              required
            />
            <span className="field-hint">Use at least 8 characters.</span>
          </div>

          <button className="submit-btn" type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Continue"}
          </button>
        </form>

        <p className="complete-registration-footer">
          Already set up? <Link to={ROUTES.LOGIN}>Login</Link>
        </p>
      </div>
    </div>
  );
};

export default CompleteRegistration;